<template>
    <div class="view-report mb-4" id="view-report-page" ref="reportPage" v-if="canView('insights')">
        <nav>
            <ol class="breadcrumb mb-2 mx-1">
                <li class="breadcrumb-item"><Link :href="'/insights/' + client.url">Insights</Link></li>
                <!-- Page selector dropdown -->
                <li class="breadcrumb-item dropdown" aria-current="page">
                    <a class="dropdown-toggle text-decoration-none" href="#" data-bs-toggle="dropdown" @click.stop style="color: #25262C;">{{ page.name }}</a>
                    <div class="dropdown-menu dropdown-menu-primary p-0 w-auto pb-2" id="report-select-dropdown-menu" data-bs-popper="none">
                        <div class="position-relative search-container p-2 mb-1 bg-white" style="position:sticky; top:0; z-index:10;">
                            <input type="text" class="search-box form-control py-1" placeholder="Search" v-model="search_page_key" @input="fetchPageOptions" @keydown.enter.prevent="selectPage(local_pages[0])" @click.stop aria-label="Search">
                            <i class="fa fa-search search-placeholder-icon" v-if="search_page_key === ''" style="left:20px"></i>
                        </div>
                        <div class="options-container">
                            <template v-for="(pages, group, idx) in pagesByGroup()" :key="group">
                                <template v-if="group !== 'Ungrouped'">
                                    <h6 class="dropdown-header d-flex justify-content-between align-items-center" @click.stop.prevent="toggleGroup(group)">
                                        <span>{{ group }}</span>
                                        <i :class="collapsed_groups[group] ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                                    </h6>
                                </template>
                                <div v-show="!collapsed_groups[group]">
                                    <Link v-for="p in pages" :key="p.id" :href="'/insights/' + client.url + '/' + p.url" class="dropdown-item py-1 ms-2" :class="{active: p.id === page.id}">
                                        {{ p.name }}
                                    </Link>
                                </div>
                                <!-- <div class="dropdown-divider" v-if="idx !== Object.keys(pagesByGroup()).length - 1"></div> -->
                            </template>
                            <span v-if="local_pages.length === 0" class="py-1 px-2">No pages match your search</span>
                        </div>
                    </div>
                </li>
            </ol>
        </nav>
        <div class="row mx-0">
            <div :class="mainContentClass" class="p-0">
                <div class="row mx-1 p-0 mb-2">
                    <div class="col-lg-8 col-12 px-0">
                        <span class="h2">{{ page.name }}</span>
                        <img class="info-icon ms-2" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" :title="page.description" alt="">
                    </div>

                    <div class="col-lg-4 col-12 px-0">
                        <button type="button" class="btn btn-primary float-end ms-2 mb-2" @click="toggleFilterPanel()">
                            <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">
                            <span class="ps-1" v-if="!is_filter_panel_expanded && !if_mobile">Filters ({{ filter_count }})</span>
                        </button>
                        <div v-if="canExport('insights')" class="float-end ms-2 mb-2">
                            <export-charts-button v-if="!is_goals_tab_active" :page="local_page" :client="client" :user="user" @tab="setActiveTab"
                                :is_filter_panel_expanded="is_filter_panel_expanded"></export-charts-button>
                        </div>
                        <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                            <button class="btn btn-secondary ms-2" :disabled="true" id="export-button">
                                <img class="icon pe-0 mb-0"  src="/img/icons/avid-icons/presention-chart.svg" alt="Presentation Icon">
                                <span class="ps-1" v-if="!if_mobile">Generate Slides</span>
                            </button>
                        </div>
                        
                        <!-- Page-level Data Labels Toggle Button - positioned right before Generate Slides -->
                        <div class="float-end ms-2 mb-2">
                            <div class="chart-labels-toggle-container-page">
                                <button 
                                    class="btn btn-secondary labels-toggle-btn btn-icon-only-md" 
                                    @click="togglePageDataLabels" 
                                    data-bs-toggle="tooltip" 
                                    :title="page_show_data_labels ? 'Hide Data Labels' : 'Show Data Labels'"
                                    :class="{ 'active': page_show_data_labels }">
                                    <div class="toggle-knob">
                                        <img src="/img/icons/avid-icons/text.svg" alt="Toggle Labels" class="icon pe-0">
                                    </div>
                                </button>
                            </div>
                        </div>

                        <button v-if="!is_goals_tab_active && activeChart && canExport('insights')" class="btn btn-secondary float-end ms-2" data-bs-toggle="tooltip" title="Export Chart" @click="downloadChart(activeChart, activeChartIndex)">
                            <img class="icon" src="/img/icons/avid-icons/file-download-01.svg" alt="">
                            <span class="ps-1" v-if="!if_mobile">Export Chart</span>
                        </button>
                        <div v-if="!is_goals_tab_active && activeChart && !canExport('insights')" class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                            <button class="btn btn-secondary ms-2" :disabled="true">
                                <img class="icon" src="/img/icons/avid-icons/file-download-01.svg" alt="">
                                <span class="ps-1"v-if="!if_mobile">Export Chart</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="row mx-1 mb-4" v-if="applied_filters && applied_filters.length > 0">
                    <div class="filter-badge-container px-0">
                        <label>Filters Applied:</label>
                        <template v-if="is_loading_filters">               
                            <div class="d-inline-block spinner-border spinner-border-sm me-0" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <span>Loading filters...</span>
                        </template>
                        <template v-else-if="applied_filters && applied_filters.length">
                            <template v-for="(filter, index) in applied_filters" :key="filter.filter_id + filter.filter_value">
                                <div v-if="filter && filter.filter_id > 0"
                                    class="badge badge-white"
                                    :class="{'badge-with-btn': !filter.is_required && filter.applies_to_section, 'opacity-50 pe-none': !filter.applies_to_section }"
                                    :data-bs-toggle="!filter.applies_to_section ? 'tooltip' : undefined"
                                    :title="!filter.applies_to_section ? 'This filter does not apply to this section' : undefined">
                                    <span v-if="filter.filter_type === 'date_range'" @click="filter.applies_to_section && navigateToFilter(filter)">
                                        {{filter.filter_label}}: {{ dateRangeDisplay(filter.filter_value) }}
                                    </span>
                                    <span v-else-if="filter.filter_type === 'month_range'" @click="filter.applies_to_section && navigateToFilter(filter)">
                                        {{filter.filter_label}}: {{ monthRangeDisplay(filter.filter_value) }}
                                    </span>
                                    <span v-else-if="filter.filter_type === 'single_select'" @click="filter.applies_to_section && navigateToFilter(filter)">
                                        {{filter.filter_label}} = "{{filter.filter_value}}"
                                    </span>
                                    <span v-else-if="filter.filter_type === 'multi_select' || filter.filter_type === 'month_picker' || filter.filter_type === 'array_single_select' || filter.filter_type === 'array_multi_select'" @click="filter.applies_to_section && navigateToFilter(filter)">
                                        {{filter.filter_label}}{{ Array.isArray(filter.filter_value) && filter.filter_value.length > 1 ? ' ('+filter.filter_value.length+')' : ' = "'+ (Array.isArray(filter.filter_value) && filter.filter_value.length ? filter.filter_value[0] : filter.filter_value) +'"'}}
                                    </span>
                                    <span v-else-if="filter.filter_type === 'numeric_slider'" @click="filter.applies_to_section && navigateToFilter(filter)">
                                        {{filter.filter_label}} between {{ filter.filter_value[0] }} and {{ filter.filter_value[1] }}
                                    </span>
                                    <button v-if="!filter.is_required && filter.applies_to_section"
                                            class="btn btn-xs p-0 ps-1"
                                            @click.stop.prevent="removeAppliedFilter(filter)">
                                        <i class="fas fa-close"></i>
                                    </button>
                                </div>
                            </template>
                        </template>
                    </div>
                </div>

                <!-- Applied Chart Filter (moved up from ReportViewSection) -->
                <div class="applied-chart-filter mb-4" :class="[applied_chart_filter != null && applied_chart_filter.if_chart_filter_applied ? '' : 'visually-hidden']">
                    <div class="applied-chart-filter-direct-descendant-div d-flex align-items-center">
                        <div v-if="applied_chart_filter != null && applied_chart_filter.if_chart_filter_applied" class="applied-chart-filter__content d-flex flex-row gap-2">
                            <h4 class="mb-0">Chart Filter:</h4>
                            <div class="badge">
                                <span>{{ applied_chart_filter.chart_selected.title }}: Audience selected: {{ getSegmentName() }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="applied-chart-filter-direct-descendant-div">
                        <div class="applied-chart-filter__actions">
                            <button v-if="canEdit('pathways') || canEdit('direct_mail')" type="button" class="btn btn-secondary" data-bs-toggle="modal" :data-bs-target="'#save-segment-modal-' + (active_page_section ? active_page_section.id : '')" @click="setSegmentDetails('audience')">
                                <img class="icon svg-filter-primary" src="/img/icons/avid-icons/save.svg" alt="">
                                <span class="ps-1" v-if="!if_mobile">Save Audience</span>
                            </button>
                            <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                <button class="btn btn-secondary" :disabled="true">
                                    <img class="icon" src="/img/icons/avid-icons/save.svg" alt="">
                                    <span class="ps-1" v-if="!if_mobile">Save Audience</span>
                                </button>
                            </div>
                            <button v-if="canExport('pathways')" class="btn btn-primary" data-bs-toggle="modal" :data-bs-target="'#push-segment-modal-' + (active_page_section ? active_page_section.id : '')" @click="setSegmentDetails">
                                <img class="icon" src="/img/icons/avid-icons/direction-sign.svg" alt="">
                                <span class="ps-1" v-if="!if_mobile">Push Audience</span>
                            </button>
                            <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                <button class="btn btn-secondary" :disabled="true">
                                    <img class="icon" src="/img/icons/avid-icons/direction-sign.svg" alt="">
                                    <span class="ps-1" v-if="!if_mobile">Push Audience</span>
                                </button>
                            </div>

                            <button type="button" class="close btn" @click="removedAppliedChartFilter">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                        </div>
                    </div>

                </div>

                <div class="row mx-1 mt-3">
                    <nav class="col-12 navbar navbar-expand primary-tab-bar d-flex">
                        <div class="collapse navbar-collapse">
                            <ul class="navbar-nav">
                                <li class="nav-item" v-for="(section, index) in local_page.sections" :key="'section-'+index">
                                    <a class="nav-link d-flex flex-nowrap primary-tab" :class="{'primary-tab-active': section.is_section_tab_active}"
                                    href="#" role="button" aria-expanded="false" @click.prevent="setActiveTab(index)">
                                        <span>{{ section.title }}</span>
                                    </a>
                                </li>
                                <li class="nav-item" v-show="local_page.allow_goals">
                                    <a class="nav-link d-flex flex-nowrap primary-tab" :class="{'primary-tab-active': is_goals_tab_active}"
                                    href="#" role="button" aria-expanded="false" @click.prevent="setActiveTab('goals')">
                                        <span>Goal Tracking</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </nav>

                    <div class="px-0" v-for="(section, index) in local_page.sections" :key="section.id" 
                            :class="(section.is_section_tab_active ? '' : 'd-none')">
                        <report-view-section
                            :key="section.id"
                            :ref="el => el && section.id && (sectionComponents[section.id] = el)"
                            :page="local_page"
                            :client="client"
                            :user="user"
                            :connections="connections"
                            :page_section="section"
                            :chart_summaries="chart_summaries"
                            :is_filter_panel_expanded="is_filter_panel_expanded"
                            :if_refreshing="if_refreshing"
                            :applied_chart_filter="applied_chart_filter"
                            :if_benchmark_report="false"
                            :if_allow_export="canExport('insights')"
                            :section_index="index"
                            :page_show_data_labels="page_show_data_labels"
                            :is_section_active="section.is_section_tab_active ?? true"
                            :should_show_summary="!(user?.preferences && user.preferences.show_ai_summaries === false)"
                            :show_compressed_numbers="user?.preferences?.show_compressed_numbers === undefined || user?.preferences?.show_compressed_numbers === null ? true : user.preferences.show_compressed_numbers === true"
                            @exploreChartInSection="exploreChartInSection"
                            @chartSelected="setActiveChart"
                            @scrollToChartInSection="scrollToChartInSection"
                            @chart-summary-queued="onChartSummaryQueued"
                            @chart-summary-completed="onChartSummaryCompleted">
                        </report-view-section>
                    </div>

                    <div v-if="is_goals_tab_active">
                        <report-goals :page="local_page" :user="user" :client="client"></report-goals>
                    </div>

                    <div v-if="!is_goals_tab_active && page.data_blend_type === 'constituent' && canEdit('pathways')" class="row mt-3">
                        <div class="d-flex align-items-center">
                            <button
                                type="button"
                                class="btn btn-link px-0"
                                data-bs-toggle="modal"
                                :data-bs-target="'#save-segment-modal-' + (active_page_section ? active_page_section.id : '')"
                                @click="setSegmentDetails('analysis_audience')"
                            >
                                Save current filters as an analysis audience
                            </button>
                        </div>
                    </div>
                    <div class="row mt-0">
                        <div class="d-flex align-items-center">
                            <h4 class="font-weight-400 font-color-tertiary mb-0 me-2">Last Transaction Date:</h4>
                            <span class="font-weight-400">
                                <span v-if="meta_data != null">
                                    <span v-if="meta_data._avid_max_display_date != null && meta_data._avid_max_display_date.date != null">
                                        {{date(meta_data._avid_max_display_date.date) }} 
                                    </span>
                                    <span v-else-if="meta_data._avid_max_display_date != null">
                                        {{date(meta_data._avid_max_display_date) }} </span>
                                    <span v-else-if="meta_data._max_overall_donation_date != null">
                                        {{date(meta_data._max_overall_donation_date) }} 
                                    </span>
                                </span>
                                <span v-else><div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"><span class="visually-hidden"></span></div> Loading</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <transition name="slide-fade" appear duration="500">
            <div class="filter-panel" :class="is_filter_panel_expanded ? 'open' : 'visually-hidden'">
                <div id="filter-panel--heading" class="p-4 pb-2">
                    <h3>Filters</h3>
                    <button type="button" class="btn btn-white float-end close-modal" @click="toggleFilterPanel()" aria-label="Close">
                        <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                </div>
                <report-view-filter-panel ref="filterPanel" :page="local_page" :applied_chart_filter="applied_chart_filter" :section_id="effective_section_id_for_filters"
                @refreshing="refreshing" @updatePage="updatePage" @removeChartFilter="removeChartFilter" @filterLoading="filterLoading"></report-view-filter-panel>
            </div>
        </transition>

        <report-page-filter-proposal-modal
            ref="reportPageFilterProposalModal"
            :client="client"
            :page="local_page"
            @accepted="onReportPageFilterAccepted"
        />


        <div class="modal fade push-segment-modal new-modal" :id="'push-segment-modal-' + (active_page_section ? active_page_section.id : '')" data-bs-backdrop="static" aria-labelledby="push-segment-modal" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content mx-2">
                    <div class="modal-header">
                        <div class="w-100 d-flex flex-column gap-1">
                            <p class="mb-0">Pathways</p>
                        </div>
                        <button type="button" class="btn btn-white float-end close-modal" :data-bs-dismiss="'push-segment-modal-' + (active_page_section ? active_page_section.id : '')" @click="closeModal('push-segment-modal-' + (active_page_section ? active_page_section.id : ''))" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                    </div>
                    <div class="modal-body pt-0">
                        <h2>Push Audience</h2>
                        <p>Fill out the details for your selected audience. Once the audience is created you can continue to push it to a pathway.</p>
                        <div>
                            <div v-if="segment_selected" class="segment-summary-item">
                                <div class="col-8">
                                    <h4>{{ segment_selected.name }} <small v-if="segment_selected.sizes">| {{ number_with_zero(getDefaultSize(segment_selected.sizes)) }} Contacts</small></h4>
                                    <p class="mb-0">Source: {{ page.data_blend.name }}</p>
                                </div>
                                <div class="col-4" v-if="if_proceed_select_pathway">
                                    <div v-if="!segment_selected.id">
                                        <div role="status" class="d-inline-block spinner-border spinner-border-sm"></div>Audience Creating...
                                    </div>
                                    <div v-else><i class="fa fa-check green"></i>Audience Created!</div>
                                </div>
                            </div>
                        </div>
                        <div v-if="!if_proceed_select_pathway" class="d-flex flex-column my-3">
                            <div>
                                <label required>Audience Name</label>
                                <input type="text" class="form-control" :class="[errors.segment_name_push !== '' ? 'is-invalid' : '']" name="segment_name" v-model="segment_selected.name" aria-label="Audience Name">
                                <div class="text-danger invalid-feedback" v-if="errors.segment_name_push !== ''">{{ errors.segment_name_push }}</div>
                            </div>
                        </div>
                        <div v-else class="d-flex flex-column my-3">
                            <div class="w-100">
                                <div>
                                    <h4>Choose a Pathway</h4>
                                </div>
                                <div class="scroll-view" style="max-height: 300px; overflow-y: auto" :class="[connections.length >= 3 ? 'pe-2' : '']">
                                    <div v-for="(conn, conn_index) in connections" :key="conn.id" class="card long-card mb-2" :class="[conn.id===pathway_selected.id ? 'long-card-selected' : '']">
                                        <div class="card-body long-card__body d-flex flex-row align-items-center" @click="changePathwaySelection(conn)">
                                            <div class="long-card__radio d-flex flex-row align-items-center">
                                                <input class="form-radio-input" type="radio" :id="conn.id" :value="conn.id" :checked="conn.id === pathway_selected.id" aria-label="Radio option">
                                            </div>
                                            <div class="long-card__content d-flex flex-row align-items-center gap-2">
                                                <div class="d-flex flex-column gap-1"><img :src="conn.integration.logo_url" :alt="conn.integration.name" class="avatar rounded-circle avatar-sm bg-white"></div>
                                                <div class="d-flex flex-column gap-1"><h4 class="mb-0">{{ conn.integration_id === '71b0c3b0-8390-49ec-9984-1c04b7384d38' ? 'File Export' : conn.name }}</h4></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="w-100 d-flex flex-row justify-content-center gap-3">
                            <button class="btn btn-secondary w-50" @click.prevent="closeModal('push-segment-modal-' + (active_page_section ? active_page_section.id : ''))">Cancel</button>
                            <button v-if="!if_proceed_select_pathway" class="btn btn-primary w-50" @click.prevent="proceedSelectPathway">Confirm and Select Pathway</button>
                            <button v-else class="btn btn-primary w-50" :disabled="!pathway_selected.id || !segment_selected.id" @click.prevent="directToPushSegmentPage">Review and Confirm</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal fade save-segment-modal new-modal" :id="'save-segment-modal-' + (active_page_section ? active_page_section.id : '')" data-bs-backdrop="static" aria-labelledby="save-segment-modal" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content mx-2">
                    <div class="modal-header">
                        <div class="w-100 d-flex flex-column gap-1">
                            <p class="mb-0">Pathways</p>
                        </div>
                        <button type="button" class="btn btn-none close-modal" :data-bs-dismiss="'save-segment-modal-' + (active_page_section ? active_page_section.id : '')" 
                        @click="closeModal('save-segment-modal-' + (active_page_section ? active_page_section.id : ''))" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                    </div>
                    <div class="modal-body pt-0">
                        <h2>{{ segment_save_mode === 'analysis_audience' ? 'Save Analysis Audience' : 'Save Audience' }}</h2>
                        <p v-if="segment_save_mode === 'analysis_audience'">Save the current filters as an analysis audience. Once it is created, you can manage it in Pathways - Manage Audiences.</p>
                        <p v-else>Save the audience that you want to save. Once the audience is created you can view it in Pathways - Manage Tab.</p>
                        <div>
                            <div v-if="segment_selected.name" class="segment-summary-item">
                                <div class="col-12">
                                    <h4>{{ segment_selected.name }} <small v-if="segment_selected.size">| {{ number_with_zero(segment_selected.size) }} Contacts</small></h4>
                                    <p class="mb-0">Source: {{ page.data_blend.name }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex flex-column my-3">
                            <div>
                                <label required>Audience Name</label>
                                <input type="text" class="form-control" :class="[errors.segment_name_save !== '' ? 'is-invalid' : '']" name="segment_name" v-model="segment_selected.name" aria-label="Audience Name">
                                <div class="text-danger invalid-feedback" v-if="errors.segment_name_save !== ''">{{ errors.segment_name_save }}</div>
                            </div>
                        </div>
                        <div class="w-100 d-flex flex-row justify-content-center gap-3 mt-3">
                            <button class="btn btn-white w-50" @click.prevent="closeModal('save-segment-modal-' + (active_page_section ? active_page_section.id : ''))">Cancel</button>
                            <button class="btn btn-primary w-50" @click.prevent="saveSegment">
                                <div v-if="if_saving_segment" class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"><span class="visually-hidden">Loading...</span></div>
                                {{ segment_save_mode === 'analysis_audience' ? 'Save Analysis Audience' : 'Save Audience' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import {Context} from "svgcanvas";
import chartExportMixin from '../../mixins/chart_export';
import ReportViewSection from './ReportViewSection.vue';
import ReportPageFilterProposalModal from './ReportPageFilterProposalModal.vue';
import { defineComponent, ref, nextTick, reactive, shallowReactive, markRaw } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import permissionsMixin from '../../mixins/permissions';
import { useNotifications } from '../../composables/useNotifications';
import { useUserStore } from '../../stores/user';

export default defineComponent({
    name: 'ReportView',
    mixins: [chartExportMixin, permissionsMixin],
    components: {
        Swal,
        ReportViewSection,
        ReportPageFilterProposalModal,
        Link
    },
    setup() {
        const reportPage = ref(null);
        const reportSections = ref([]);
        const tooltips = ref([]);
        // Use shallowReactive for section components to avoid deep reactivity on component instances
        const sectionComponents = shallowReactive({});
        const { showNotification } = useNotifications();
        const userStore = useUserStore();

        return {
            reportPage,
            reportSections,
            tooltips,
            sectionComponents,
            showNotification,
            userStore
        };
    },
    props: {
        page: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        },
        connections: {
            type: Array,
            required: true
        },
        section_id: {
            type: [String, Number],
            required: true
        },
        all_pages: {
            type: [Array, Object],
            required: false,
            default: () => []
        }
    },
    data() {
        return {
            local_page: {},
            if_benchmark_report: false,
            active_tab_index: 0,
            is_filter_panel_expanded: false,
            filter_count: 0,
            if_refreshing: false,
            is_loading_filters: false,
            applied_chart_filter: {
                if_chart_filter_applied: false,
                section_index: null,
                section_chart_id: null,
                chart_selected: {},
                chart_filters: []
            },
            x: 0,
            y: 0,
            isDragging: false,
            meta_data: null,
            params: "",
            is_goals_tab_active: false,
            active_section_id: null,
            activeChart: null,
            activeChartIndex: null,
            loading: false,
            filter: null,
            // ADD SEGMENT MODAL STATE
            errors: {
                segment_name_push: '',
                segment_name_save: ''
            },
            segment_selected: {
                name: ''
            },
            segment_save_mode: 'audience',
            if_saving_segment: false,
            pathway_selected: {},
            if_proceed_select_pathway: false,
            chart_summaries: {},
            // PAGE DROPDOWN STATE
            search_page_key: '',
            local_pages: [],
            collapsed_groups: {},
            echoChannel: null, // Store Echo channel for cleanup
            isMounted: false, // Track if component is mounted

            if_mobile: window.innerWidth < 768,
            sidebar_collapsed: localStorage.getItem('sidebar_collapsed') === 'true',
            page_show_data_labels: (() => {
                const stored = sessionStorage.getItem('insights_show_data_labels');
                return stored !== null ? stored === 'true' : false;
            })(),
        };
    },
    beforeMount() {
        this.local_page = this.page;
        // Initialize local_pages list for dropdown
        if (Array.isArray(this.all_pages)) {
            this.local_pages = [...this.all_pages];
        } else if (this.all_pages && typeof this.all_pages === 'object' && this.all_pages.data) {
            // If it's a Laravel collection sent as object with data prop
            this.local_pages = [...this.all_pages.data];
        }
        if (this.local_page.data_blend_type.startsWith('benchmark'))
            this.if_benchmark_report = true;

        // Set default dimension filter values
        this.setDefaultFilterValues();

        if (this.local_page.cache_only === true && this.hasEmptyCharts())
            this.applyChartFilter();

        this.getFilterCount();
        this.loadSectionTab();

        if (!this.if_benchmark_report) {
            this.loadMetaData();
        }

        // Collapse all groups initially
        this.$nextTick(() => {
            Object.keys(this.pagesByGroup()).forEach(g => {
                this.collapsed_groups[g] = true;
            });
        });

        // Handle deep links for goals/section tabs
        if (window.location.pathname.endsWith('/0')) {
            this.setActiveTab('goals');
        } else if (this.section_id > 0) {
            this.active_section_id = this.section_id;
            const idx = this.local_page.sections.findIndex(s => s.id == this.section_id);
            this.setActiveTab(idx >= 0 ? idx : 0);
        } else {
            this.setActiveTab(0);
        }
    },

    mounted() {
        this.isMounted = true;
        
        // Keep mobile flag reactive
        this.onResize = () => {
            this.if_mobile = window.innerWidth < 768;
        };
        window.addEventListener('resize', this.onResize);
        // Run once in case initial flag needs update after render
        this.onResize();
        window.addEventListener('storage', this.handleSidebarStorageChange);
        this.sidebar_collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
        window.addEventListener('sidebar-toggle', this.handleSidebarToggleEvent);

        // Fetch key takeaways for all sections once after component is mounted
        if (!this.if_benchmark_report) {
            this.$nextTick(() => {
                this.fetchAllSectionsKeyTakeaways();
                this.setupKeyTakeawaysListener();
            });
        }

        this.handleOpenReportPageFilterProposal = (event) => {
            const action = event.detail || event;
            if (action && action.type === 'openReportPageFilterProposal' && action.report_page_filter_suggestion_id) {
                const modal = this.$refs.reportPageFilterProposalModal;
                if (modal && typeof modal.show === 'function') {
                    modal.show(action.report_page_filter_suggestion_id);
                }
            }
        };
        window.addEventListener('openReportPageFilterProposal', this.handleOpenReportPageFilterProposal);
    },

    beforeUnmount() {
        this.isMounted = false;
        
        // Dispose tooltips
        if (this.tooltips && this.tooltips.length) {
            this.tooltips.forEach(t => {
                try { t.hide(); } catch (e) {}
            });
        }
        document.removeEventListener('click', this.handleDocumentClickForTooltip);
        window.removeEventListener('scroll', this.hideAllTooltips, true);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('storage', this.handleSidebarStorageChange);
        window.removeEventListener('sidebar-toggle', this.handleSidebarToggleEvent);
        if (this.handleOpenReportPageFilterProposal) {
            window.removeEventListener('openReportPageFilterProposal', this.handleOpenReportPageFilterProposal);
        }
        
        // Clean up Echo listener - stop listening first
        if (this.echoChannel) {
            try {
                this.echoChannel.stopListening('KeyTakeawaysUpdated');
                this.echoChannel.stopListening('ChartSummaryUpdated');
            } catch (e) {
                // Silently continue
            }
            this.echoChannel = null;
        }
        
        // Clean up component references - reactive object will be cleared on unmount
        Object.keys(this.sectionComponents).forEach(key => {
            delete this.sectionComponents[key];
        });
    },
    computed: {
        user() {
            return this.userStore.getCurrentUser;
        },
        date_type() {
            if(this.page.data_blend_type == 'constituent')
                return "Transaction ";
            else if(this.page.data_blend_type == 'subscriber')
                return "Email ";
            return "";
        },
        applied_filters() {
            let applied_filters_list = [];

            if (!this.local_page || !this.local_page.dimensions || !this.local_page.dimensions.length) {
                return applied_filters_list;
            }

            for (var i = 0; i <this.local_page.dimensions.length; i++){
                let dim = this.local_page.dimensions[i];

                // Skip filters that should be hidden
                if (this.shouldHideFilter(dim)) {
                    continue;
                }

                //Todo: consolidate the default_value and value checks
                if( (
                    //If it has a default value
                    dim.default_value && (dim.default_value.length  ||
                    //Or is a date/month range with some values set
                    ((dim.field_type == 'date_range'|| dim.field_type == 'month_range') && (dim.default_value.end_date != null || dim.default_value.start_date != null))))
                    //Or it has a value
                    || (dim.value && (dim.value.length ||
                        //Or is a date/month range with some values set
                        ((dim.field_type == 'date_range'|| dim.field_type == 'month_range') && (dim.value.end_date != null || dim.value.start_date != null))
                    ))){
                        applied_filters_list.push({
                            filter_id: dim.id,
                            filter_label: dim.label,
                            filter_value: (dim.value != null ? dim.value : dim.default_value),
                            //If it is year_type, that needs to come from the client, not the dim
                            filter_default_value: ((typeof dim.column === 'string' || dim.column instanceof String) && dim.column.indexOf('time_period.year_type') !== -1? this.page.client.default_reporting_period : dim.default_value),
                            filter_column_name: dim.column,
                            filter_type: dim.field_type,
                            is_required: dim.is_required,
                            group_index: dim.filter_group && this.local_page.sections ? this.local_page.sections.findIndex(section => section.id == dim.filter_group) : 0,
                            applies_to_section: this.dimensionAppliesToCurrentSection(dim)
                        });
                    }
                }
            return applied_filters_list;
        },
        active_page_section() {
            if (this.is_goals_tab_active) return null;
            const sections = this.local_page?.sections;
            if (!Array.isArray(sections) || typeof this.active_tab_index !== 'number') {
                return null;
            }
            return sections[this.active_tab_index] || null;
        },
        /** Section id for the currently active tab, so the filter panel reacts when switching sections in-page. */
        effective_section_id_for_filters() {
            if (this.active_page_section && this.active_page_section.id != null) return this.active_page_section.id;
            return null;
        },
        mainContentClass() {
            return {
                'has-filter-panel-open': this.is_filter_panel_expanded,
                'has-filter-panel-closed': !this.is_filter_panel_expanded,
                'sidebar-collapsed': this.sidebar_collapsed,
                'sidebar-expanded': !this.sidebar_collapsed,
            };
        },
    },
    watch: {
        section_id: {
            handler(newVal) {
                if (newVal > 0){
                    var index = this.local_page.sections.findIndex(section => section.id == newVal);
                    if (index >= 0) {
                        this.setActiveTab(index);
                    } else {
                        this.setActiveTab(0);
                    }
                }
            }
        },
        'local_page.dimensions': {
            handler() {
                // Recalculate filter count when dimensions change (e.g., when filter options are loaded)
                this.$nextTick(() => {
                    this.getFilterCount();
                });
            },
            deep: true
        }
    },
    methods: {
        dimensionAppliesToCurrentSection(dim) {
            const group = dim.report_page_dimensional_filter_group;
            if (!group || !group.section_ids || !Array.isArray(group.section_ids) || group.section_ids.length === 0) return true;
            const currentSectionId = this.active_page_section ? this.active_page_section.id : (this.section_id ? parseInt(this.section_id, 10) : null);
            if (currentSectionId == null || Number.isNaN(currentSectionId)) return true;
            return group.section_ids.some(id => Number(id) === currentSectionId);
        },
        shouldHideFilter(dim) {
            // Hide filter if it's multi_select, has only 1 option, and that option is the default value
            if (!dim || (dim.field_type !== 'multi_select' && dim.field_type !== 'array_single_select' && dim.field_type !== 'array_multi_select')) {
                return false;
            }
            
            // Get options and default_value from dim or original page dimensions
            let options = dim.options;
            let defaultValue = dim.default_value;
            
            // If options or default_value not available, check original page dimensions
            if (!options || !Array.isArray(options) || options.length === 0) {
                if (this.page && this.page.dimensions) {
                    const originalDim = this.page.dimensions.find(d => d.id === dim.id);
                    if (originalDim && originalDim.options && Array.isArray(originalDim.options) && originalDim.options.length > 0) {
                        options = originalDim.options;
                    } else {
                        // Options not loaded yet, can't determine if should hide
                        return false;
                    }
                } else {
                    // Options not loaded yet, can't determine if should hide
                    return false;
                }
            }
            
            if (!defaultValue || !Array.isArray(defaultValue) || defaultValue.length === 0) {
                if (this.page && this.page.dimensions) {
                    const originalDim = this.page.dimensions.find(d => d.id === dim.id);
                    if (originalDim && originalDim.default_value && Array.isArray(originalDim.default_value) && originalDim.default_value.length > 0) {
                        defaultValue = originalDim.default_value;
                    } else {
                        // Default value not available, can't determine if should hide
                        return false;
                    }
                } else {
                    // Default value not available, can't determine if should hide
                    return false;
                }
            }
            
            // Check if filter should be hidden
            if (options && Array.isArray(options) && options.length === 1 &&
                defaultValue && Array.isArray(defaultValue) && defaultValue.length === 1 &&
                options[0] === defaultValue[0]) {
                return true;
            }
            
            return false;
        },
        loadMetaData() {
            var data = {
                client: this.client,
                blend_type: this.page.data_blend_type
            };

            window.axios.post('/api/bigquery/get_metadata', data)
                .then(response => {
                this.meta_data = response.data.metadata;
            });
        },

        hasEmptyCharts(){
            return this.local_page.sections.some(section => section.charts.some(chart => chart.chart.data == null && chart.chart.type != 'information_card'));
        },
        resolveRelativeDateDefault(defaultValue, fieldType) {
            const startKey = defaultValue.start || null;
            const endKey = defaultValue.end || null;
            const isMonthRange = fieldType === 'month_range';
            const startDate = this.relativeKeyToDate(startKey, isMonthRange, true);
            const endDate = this.relativeKeyToDate(endKey, isMonthRange, false);
            return {
                start_date: startDate ? moment(startDate).utc().format('YYYY-MM-DD') : null,
                end_date: endDate ? moment(endDate).utc().format('YYYY-MM-DD') : null
            };
        },
        relativeKeyToDate(key, isMonthRange, isStart) {
            if (!key) return null;
            let d;
            const now = moment();
            switch (key) {
                case 'today': d = now.clone(); break;
                case 'yesterday': d = now.clone().subtract(1, 'day'); break;
                case '1_week_ago': d = now.clone().subtract(1, 'week'); break;
                case '1_month_ago': d = now.clone().subtract(1, 'month'); break;
                case '1_year_ago': d = now.clone().subtract(1, 'year'); break;
                case '2_years_ago': d = now.clone().subtract(2, 'years'); break;
                case '3_years_ago': d = now.clone().subtract(3, 'years'); break;
                default: return null;
            }
            if (!d) return null;
            if (isMonthRange) {
                return isStart ? d.clone().startOf('month').toDate() : d.clone().endOf('month').toDate();
            }
            return d.toDate();
        },
        setDefaultFilterValues() {
            for (var i = 0; i <this.local_page.dimensions.length; i++){
                let dim = this.local_page.dimensions[i];

                //If there is value set...
                if((dim.field_type != 'date_range' && dim.field_type != 'month_range' && dim.value && dim.value.length != 0) ||
                    ((dim.field_type == 'date_range' || dim.field_type == 'month_range') && dim.value && (dim.value.start_date || dim.value.end_date))) {
                    //Do nothing

                }
                //If there is a default value option, set it to that
                else if((dim.field_type != 'date_range' && dim.field_type != 'month_range' && dim.default_value && dim.default_value.length != 0) ||
                ((dim.field_type == 'date_range' || dim.field_type == 'month_range') && dim.default_value && (!dim.value || (dim.default_value.start_date && !dim.value.start_date) || (dim.default_value.end_date && !dim.value.end_date) || (dim.default_value.type === 'relative'))) ) {
                    if ((dim.field_type === 'date_range' || dim.field_type === 'month_range') && dim.default_value && dim.default_value.type === 'relative') {
                        this.local_page.dimensions[i].value = this.resolveRelativeDateDefault(dim.default_value, dim.field_type);
                    } else {
                        this.local_page.dimensions[i].value = dim.default_value;
                    }
                }
                //No value or default value, so set it to something blank
                else {
                    if(dim.field_type == "multi_select" || dim.field_type == "month_picker" || dim.field_type == "array_single_select" || dim.field_type == "array_multi_select")
                        this.local_page.dimensions[i].value =  [];
                    else if(dim.field_type == "single_select")
                        this.local_page.dimensions[i].value = "";
                    else if(dim.field_type == "date_range" || dim.field_type == "month_range")
                        this.local_page.dimensions[i].value = {
                            'start_date': null,
                            'end_date': null
                        }
                }
            }
        },
        loadSectionTab(){
            for(var i=0; i<this.local_page.sections.length; i++){
                if(i == 0){
                    this.local_page.sections[i].is_section_tab_active = true;
                } else {
                    this.local_page.sections[i].is_section_tab_active = false;
                }
            }
        },
        setActiveTab(index) {
            // Use nextTick to batch updates
            nextTick(() => {
                if (index === 'goals') {
                    // Handle goals tab
                    this.is_goals_tab_active = true;
                    this.active_section_id = 'goals';
                    const sectionsGoals = this.local_page?.sections;
                    if (sectionsGoals && Array.isArray(sectionsGoals)) {
                        sectionsGoals.forEach(section => {
                            section.is_section_tab_active = false;
                        });
                    }
                } else {
                    // Handle regular section tabs
                    this.is_goals_tab_active = false;

                    const sections = this.local_page?.sections;
                    if (!sections || !Array.isArray(sections) || sections.length === 0) {
                        this.active_tab_index = index;
                        return;
                    }

                    // Make sure index is valid
                    if (index < 0 || index >= sections.length) {
                        index = 0;
                    }

                    // Update section states in place
                    sections.forEach((section, i) => {
                        section.is_section_tab_active = i === index;
                    });
                    
                    this.active_section_id = sections[index].id;
                }

                this.active_tab_index = index;

                // Remove any existing tooltips
                [...document.querySelectorAll('.chart-template-tooltip')].forEach(t => t.remove());

                // Dismiss Bootstrap tooltips
                const tooltips = document.querySelectorAll('[data-bs-toggle="tooltip"]');
                tooltips.forEach(tooltipEl => {
                    const tooltip = bootstrap.Tooltip.getInstance(tooltipEl);
                    if (tooltip) {
                        tooltip.hide();
                    }
                });

                //Get the current query parameters
                this.params = window.location.search || "";

                // Update URL without triggering a re-render
                const sectionsForUrl = this.local_page?.sections;
                const newURL = '/insights/' + this.client.url + '/' + this.page.url + '/' +
                    (index === 'goals' ? '0' : (sectionsForUrl?.[index]?.id ?? '0')) + this.params;
                window.history.replaceState({}, '', newURL);

                //Also append the params to the export buttons
                let export_btn = document.getElementById('export-button');
                if(export_btn && index !== 'goals' && sectionsForUrl?.[index]) {
                    export_btn.href = '/export/' + this.client.url + '/' + this.page.url + '/' +
                        sectionsForUrl[index].id + this.params;
                }

                //Save what section they were looking at
                if (index !== 'goals' && sectionsForUrl?.[this.active_tab_index]) {
                    const data = {
                        client_id: this.client.id,
                        report_page_id: this.page.id,
                        report_page_section_id: sectionsForUrl[this.active_tab_index].id
                    };
                    window.axios.post('/api/insights/history', data);
                }

                // Initialize tooltips after the DOM has updated
                nextTick(() => {
                    this.initializeTooltips();
                });
            });
        },
        initializeTooltips() {
            // Hide previously created tooltips (do NOT dispose).
            // Disposing a Bootstrap tooltip sets internal properties (e.g. _activeTrigger)
            // to null which can lead to race-condition errors when internal delegated
            // event handlers fire shortly afterwards.
            // Instead, we simply hide the tooltip and let it be reused if needed.
            if (this.tooltips && this.tooltips.length) {
                this.tooltips.forEach(tip => {
                    try { tip.hide(); } catch (e) {}
                });
                this.tooltips = [];
            }

            const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
            const triggerType = isTouchDevice ? 'click' : 'hover';

            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            tooltipTriggerList.forEach(el => {
                const tip = new bootstrap.Tooltip(el, { trigger: triggerType, container: 'body' });
                this.tooltips.push(tip);
            });

            // For touch devices, hide tooltips when tapping elsewhere or scrolling
            if (isTouchDevice) {
                document.removeEventListener('click', this.handleDocumentClickForTooltip);
                document.addEventListener('click', this.handleDocumentClickForTooltip);

                window.removeEventListener('scroll', this.hideAllTooltips, true);
                window.addEventListener('scroll', this.hideAllTooltips, true);
            }
        },
        hideAllTooltips() {
            if (this.tooltips && this.tooltips.length) {
                this.tooltips.forEach(tip => tip.hide());
            }
        },
        handleDocumentClickForTooltip(event) {
            // If the click is outside any tooltip trigger element, hide all
            if (!event.target.closest('[data-bs-toggle="tooltip"]')) {
                this.hideAllTooltips();
            }
        },
        toggleFilterPanel(){
            this.is_filter_panel_expanded = !this.is_filter_panel_expanded
        },
        togglePageDataLabels(event) {
            const newPageValue = !this.page_show_data_labels;
            this.page_show_data_labels = newPageValue;
            sessionStorage.setItem('insights_show_data_labels', String(newPageValue));

            // Update all charts in all sections
            this.local_page.sections.forEach(section => {
                const sectionComponent = this.sectionComponents[section.id];
                if (sectionComponent && sectionComponent.local_charts) {
                    sectionComponent.local_charts.forEach(chart => {
                        chart.show_data_labels = newPageValue;
                    });
                }
            });

            // Hide tooltip
            if (event && event.currentTarget) {
                const button = event.currentTarget;
                if (window.bootstrap && window.bootstrap.Tooltip) {
                    const tooltip = window.bootstrap.Tooltip.getInstance(button);
                    if (tooltip) {
                        tooltip.hide();
                    }
                }
                button.blur();
            }
        },
        refreshing(if_refreshing){
            this.if_refreshing = if_refreshing;
        },
        onReportPageFilterAccepted(data) {
            if (!data || !data.dimensional_filter) return;

            const newDim = data.dimensional_filter;

            // Add the new filter to the page dimensions and filter panel UI
            if (!this.local_page.dimensions) {
                this.local_page.dimensions = [];
            }
            if (!this.local_page.dimensions.some(d => d.id === newDim.id)) {
                this.local_page.dimensions.push(newDim);
            }

            // Tell the filter panel to add and load options for the new filter
            if (this.$refs.filterPanel) {
                this.$refs.filterPanel.addAcceptedFilter(newDim);
            }

            this.getFilterCount();

            // Only refresh data if the new filter has a default value
            const hasDefault = newDim.default_value != null
                && (Array.isArray(newDim.default_value) ? newDim.default_value.length > 0 : newDim.default_value !== '');

            if (hasDefault) {
                this.$nextTick(() => {
                    if (this.$refs.filterPanel) {
                        this.$refs.filterPanel.applyFilter();
                    }
                });
            }
        },
        filterLoading(is_loading){
            this.is_loading_filters = is_loading;
        },
        getFilterCount(){
            this.filter_count = 0;
            for (var i = 0; i <this.local_page.dimensions.length; i++){
                let dim = this.local_page.dimensions[i];
                
                // Skip chart filters (negative IDs)
                if (dim.id < 0) {
                    continue;
                }
                
                // Skip filters that should be hidden
                if (this.shouldHideFilter(dim)) {
                    continue;
                }
                
                let value = dim.default_value;

                if(dim.field_type != 'date_range' && dim.field_type != 'month_range' && dim.value && dim.value.length != 0)
                    value = dim.value;

                if((dim.field_type == 'date_range' || dim.field_type == 'month_range'  || dim.field_type == 'month_picker') && dim.value)
                    value = dim.value;

                if((dim.field_type == 'date_range' || dim.field_type == 'month_range') && (value && (value.start_date || value.end_date)))
                    this.filter_count++;

                if(dim.field_type != 'date_range' && dim.field_type != 'month_range' && value && value.length != 0 ){
                    this.filter_count++;
                }
            }
        },
        dateRangeDisplay(value) {
            if (!value) return '';
            if (value.start_date && value.end_date) {
                return this.formatUtcDate(value.start_date) + ' - ' + this.formatUtcDate(value.end_date);
            } else if (value.start_date) {
                return 'On or After ' + this.formatUtcDate(value.start_date);
            } else if (value.end_date) {
                return 'Prior to ' + this.formatUtcDate(value.end_date);
            }
            return '';
        },
        monthRangeDisplay(value) {
            if (!value) return '';
            if (value.start_date && value.end_date) {
                return this.formatUtcMonth(value.start_date) + ' - ' + this.formatUtcMonth(value.end_date);
            } else if (value.start_date) {
                return 'On or After ' + this.formatUtcMonth(value.start_date);
            } else if (value.end_date) {
                return 'Prior to ' + this.formatUtcMonth(value.end_date);
            }
            return '';
        },
        formatUtcDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const offset = date.getTimezoneOffset();
            date.setMinutes(date.getMinutes() + offset);
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            const yyyy = date.getFullYear();
            return `${mm}/${dd}/${yyyy}`;
        },
        formatUtcMonth(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const offset = date.getTimezoneOffset();
            date.setMinutes(date.getMinutes() + offset);
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            return `${month} ${year}`;
        },
        removeAppliedFilter(filter) {
            if (!filter) return;
            
            if (filter.filter_id === -1 || (this.applied_chart_filter.if_chart_filter_applied && filter.filter_value && (typeof filter.filter_value === 'string' || Array.isArray(filter.filter_value)) && filter.filter_value.includes(this.applied_chart_filter.chart_filters[0].value))) {
                this.local_applied_chart_filter = {
                    if_chart_filter_applied: false,
                };
            }
            // Remove the filter from the local_page
            let currentFilterIndex = this.local_page.dimensions.findIndex(d => d.id == filter.filter_id);

            if (currentFilterIndex < 0) return;

            if (this.local_page.dimensions[currentFilterIndex].field_type === 'single_select') {
                this.local_page.dimensions[currentFilterIndex].value = '';
            } else if (this.local_page.dimensions[currentFilterIndex].field_type === 'multi_select' || this.local_page.dimensions[currentFilterIndex].field_type === 'month_picker' || this.local_page.dimensions[currentFilterIndex].field_type === 'array_single_select' || this.local_page.dimensions[currentFilterIndex].field_type === 'array_multi_select') {
                this.local_page.dimensions[currentFilterIndex].value = [];
            } else if (this.local_page.dimensions[currentFilterIndex].field_type === 'numeric_slider') {
                this.local_page.dimensions[currentFilterIndex].value = null;
            } else if (this.local_page.dimensions[currentFilterIndex].field_type === 'date_range' || this.local_page.dimensions[currentFilterIndex].field_type === 'month_range') {
                this.local_page.dimensions[currentFilterIndex].value = {
                    start_date: null,
                    end_date: null
                };
            }

            // Force the filter panel to detect the change and apply filters
            this.$nextTick(() => {
                if (this.$refs.filterPanel) {
                    this.$refs.filterPanel.checkAndApplyFilters();
                }
            });
        },
        removeChartFilter(){
            this.applied_chart_filter = {
                if_chart_filter_applied: false,
                section_index: null,
                section_chart_id: null,
                chart_selected: {},
                chart_filters: []
            }
        },
        updatePage(new_page){
            this.if_refreshing = false;

            // Preserve dimensions and chart sizes before replacing the page
            const { dimensions_to_preserve, chart_filters_to_preserve } = this.getDimensionsToPreserve();
            const chartSizesMap = this.preserveChartSizes();

            // Preserve goal data — the refresh endpoint does not return the goal relation
            const currentGoal = this.local_page.goal;

            // Replace the page
            this.local_page = new_page;
            this.local_page.id = this.page.id;

            if (currentGoal && !this.local_page.goal) {
                this.local_page.goal = currentGoal;
            }

            // Restore preserved data
            this.restoreChartSizes(chartSizesMap);
            this.mergePreservedDimensions(dimensions_to_preserve, chart_filters_to_preserve);

            // Update UI state
            this.getFilterCount();
            this.forceSectionUpdatesAndScroll();
            this.setActiveTab(this.active_tab_index);
            this.updateChartIfFilterApplied();

            // Reset chart summaries and re-fetch for the new page data
            this.chart_summaries = {};
            if (!this.if_benchmark_report) {
                this.$nextTick(() => {
                    this.fetchAllSectionsKeyTakeaways();
                });
            }

            this.showNotification({
                text: '<strong>' + this.local_page.name + '\'s</strong> data has been refreshed!'
            });
        },
        getDimensionsToPreserve() {
            const old_dimensions = this.local_page.dimensions || [];
            const dimensions_to_preserve = old_dimensions.filter(d => {
                // Preserve chart filters (negative IDs) and any dimension with a value
                if (d.id < 0) return true; // Chart filters
                if (d.value) {
                    // Check if value has actual content
                    if (Array.isArray(d.value) && d.value.length > 0) return true;
                    if (typeof d.value === 'string' && d.value !== '') return true;
                    if (typeof d.value === 'object' && (d.value.start_date || d.value.end_date)) return true;
                }
                return false;
            });
            
            const chart_filters_to_preserve = dimensions_to_preserve.filter(d => d.id < 0);
            
            return { dimensions_to_preserve, chart_filters_to_preserve };
        },
        preserveChartSizes() {
            const chartSizesMap = new Map();
            if (this.local_page.sections && Array.isArray(this.local_page.sections)) {
                this.local_page.sections.forEach(section => {
                    if (section.charts && Array.isArray(section.charts)) {
                        section.charts.forEach(chart => {
                            if (chart.id && chart.size) {
                                chartSizesMap.set(chart.id, chart.size);
                            }
                        });
                    }
                });
            }
            return chartSizesMap;
        },
        restoreChartSizes(chartSizesMap) {
            if (chartSizesMap.size > 0 && this.local_page.sections && Array.isArray(this.local_page.sections)) {
                this.local_page.sections.forEach(section => {
                    if (section.charts && Array.isArray(section.charts)) {
                        section.charts.forEach(chart => {
                            if (chart.id && chartSizesMap.has(chart.id)) {
                                const preservedSize = chartSizesMap.get(chart.id);
                                chart.size = preservedSize;
                                // Also set chart.chart.size so chart templates can access it
                                if (chart.chart) {
                                    chart.chart.size = preservedSize;
                                }
                            }
                        });
                    }
                });
            }
        },
        mergePreservedDimensions(dimensions_to_preserve, chart_filters_to_preserve) {
            if (dimensions_to_preserve.length === 0) return;
            
            const new_dimensions = this.local_page.dimensions || [];
            const existingIds = new Set(new_dimensions.map(d => d.id));
            
            // Add chart filters (negative IDs) - these are always preserved
            chart_filters_to_preserve.forEach(cf => {
                if (!existingIds.has(cf.id)) {
                    new_dimensions.push(cf);
                }
            });
            
            // Preserve regular dimension filters (positive IDs) that have values
            // These might be missing from new_page after chart filter is applied
            dimensions_to_preserve.forEach(preservedDim => {
                // Skip chart filters (already handled above)
                if (preservedDim.id < 0) return;
                
                // Check if this dimension exists in new_dimensions
                const existingIndex = new_dimensions.findIndex(d => d.id === preservedDim.id);
                
                if (existingIndex >= 0) {
                    // Dimension exists - only restore the old value if the new dimension has no value
                    // (the new page from the filter panel already has the user's latest selections)
                    if (!this.hasDimensionValue(new_dimensions[existingIndex].value)
                        && preservedDim.value && this.hasDimensionValue(preservedDim.value)) {
                        new_dimensions[existingIndex].value = preservedDim.value;
                    }
                } else {
                    // Dimension doesn't exist in new_page - add it back
                    new_dimensions.push({ ...preservedDim });
                }
            });
            
            this.local_page.dimensions = new_dimensions;
        },
        hasDimensionValue(value) {
            if (Array.isArray(value) && value.length > 0) return true;
            if (typeof value === 'string' && value !== '') return true;
            if (typeof value === 'object' && (value.start_date || value.end_date)) return true;
            return false;
        },
        forceSectionUpdatesAndScroll() {
            this.$nextTick(() => {
                // Use sectionComponents map instead of $refs
                Object.values(this.sectionComponents).forEach(section => {
                    if (section) {
                        section.$forceUpdate();
                    }
                });
                
                // Use setTimeout to ensure the scroll happens after all re-renders are settled
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 1000);
            });
        },
        updateChartIfFilterApplied() {
            if (!this.applied_chart_filter.if_chart_filter_applied) return;
            
            const section = this.local_page.sections[this.applied_chart_filter.section_index];
            if (!section || !section.charts) return;
            
            const selected_chart_index = section.charts.findIndex(
                chart => chart.chart.id == this.applied_chart_filter.chart_selected.id
            );
            
            if (selected_chart_index >= 0) {
                section.charts[selected_chart_index].chart = this.applied_chart_filter.chart_selected;
            }
        },
        scrollToChartInSection(details) {
            const targetSectionId = details.section_id;
            const targetChartId = details.chart_id;
            
            // Find the section index
            const sectionIndex = this.local_page.sections.findIndex(s => s.id == targetSectionId);
            
            if (sectionIndex === -1) {
                console.warn('Section not found:', targetSectionId);
                return;
            }
            
            // Switch to the target section tab
            this.setActiveTab(sectionIndex);
            
            // Wait for the section to render, then scroll to chart
            this.$nextTick(() => {
                setTimeout(() => {
                    const chartElement = document.getElementById(targetChartId);
                    if (chartElement) {
                        // Find the card inside the size-container
                        const cardElement = chartElement.querySelector('.chart-container.card');
                        const elementToHighlight = cardElement || chartElement;
                        
                        // Brief highlight on the card
                        elementToHighlight.style.transition = 'box-shadow 0.3s ease';
                        elementToHighlight.style.boxShadow = '0 0 20px rgba(0, 123, 255, 0.5)';
                        setTimeout(() => {
                            elementToHighlight.style.boxShadow = '';
                        }, 2000);
                        
                        // Scroll to chart container
                        chartElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                        console.warn('Chart element not found:', targetChartId);
                    }
                }, 100); // Small delay to ensure section is rendered
            });
        },
        exploreChartInSection(details){
            if(details.chart_filters.length == 0){
                // First, we will remove all the filters in dimension list with id == -1
                this.local_page.dimensions = [...this.local_page.dimensions.filter(d => d.id != -1)];

                // Then, if the filter is one of the user-defined filter, we will reset the dimensional filter
                var index_array = []
                this.applied_chart_filter.chart_selected.group_by.forEach((dimension) =>{
                    var index = this.local_page.dimensions.findIndex(d => d.column == dimension.value);
                    if (index>=0)
                        index_array.push(index);
                });

                index_array.forEach((index) => {
                    if(this.local_page.dimensions[index].field_type == 'single_select'){
                        this.local_page.dimensions[index].value = "";
                    }
                    if(this.local_page.dimensions[index].field_type == 'multi_select' || this.local_page.dimensions[index].field_type == 'array_single_select' || this.local_page.dimensions[index].field_type == 'array_multi_select'){
                        this.local_page.dimensions[index].value = [];
                    }
                })

                this.applied_chart_filter = {
                    if_chart_filter_applied: false,
                    section_index: null,
                    section_chart_id: null,
                    chart_selected: {},
                    chart_filters: []
                }
            } else{
                var section_chart = this.local_page.sections[details.section_index].charts.find(c=> c.chart.id == details.chart_selected.id);
                this.applied_chart_filter = {
                    if_chart_filter_applied: true,
                    section_index: details.section_index,
                    section_chart_id: section_chart.id,
                    chart_selected: details.chart_selected,
                    chart_filters: details.chart_filters,
                }
            }
            this.applyChartFilter()
        },
        applyChartFilter(){
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.if_refreshing = true;

            // Add chart filter
            if(this.applied_chart_filter.if_chart_filter_applied){
                // check if this is an existing user-defined filter option
                this.applied_chart_filter.chart_filters.forEach((chart_filter) => {
                    var index_in_dimension = this.local_page.dimensions.findIndex(d => d.column == chart_filter.column);

                    // Case 1: if the filter was not in the dimension list
                    if (index_in_dimension < 0){ // not found, add as a new user-defined filter
                        this.local_page.dimensions.push({
                            //Make a random negative number
                            id: -Math.floor(Math.random() * 1000000),
                            column: chart_filter.column,
                            default_value: null,
                            field_type:  "single_select",
                            filter_group: null,
                            is_required: 0,
                            label: 'Chart Filter',
                            order: 1,
                            report_page_id: this.local_page.id,
                            value: chart_filter.value
                        })
                    } else {
                        // Case 2: if the filter is one of the user-defined filter
                        for (var i = 0; i <this.local_page.dimensions.length; i++){
                            if (i == index_in_dimension){
                                if(this.local_page.dimensions[i].field_type == 'single_select'){
                                    this.local_page.dimensions[i].value = chart_filter.value;
                                }
                                if(this.local_page.dimensions[i].field_type == 'multi_select' || this.local_page.dimensions[i].field_type == 'array_single_select' || this.local_page.dimensions[i].field_type == 'array_multi_select'){
                                    if (this.local_page.dimensions[i].value && this.local_page.dimensions[i].value.length !=0){
                                        if (this.local_page.dimensions[i].value.includes(chart_filter.value)){
                                            this.local_page.dimensions[i].value = [chart_filter.value];
                                        } else {
                                            this.local_page.dimensions[i].value.push(chart_filter.value);
                                        }
                                    } else{
                                        this.local_page.dimensions[i].value = [chart_filter.value];
                                    }
                                }
                            }
                        }
                    }
                });
            }

            if(this.local_page.cache_only == false) {
                Swal.close();
            }
            // Send a lightweight copy — strip chart data/labels (regenerated server-side)
            // and the client relation to avoid sending hundreds of KB of unused payload.
            const payload = JSON.parse(JSON.stringify(this.local_page));
            delete payload.client;
            if (payload.sections) {
                payload.sections.forEach(section => {
                    if (section.charts) {
                        section.charts.forEach(chart => {
                            if (chart.chart) {
                                delete chart.chart.data;
                                delete chart.chart.labels;
                                delete chart.chart.metric_formats;
                            }
                        });
                    }
                });
            }
            window.axios.post('/api/insights/page/' + this.local_page.id, payload)
                .then(response => {
                    var page_temp = this.local_page;
                    // this.local_page = response.data.page;
                    this.local_page.dimensions = page_temp.dimensions
                    this.if_refreshing = false;
                    this.updatePage(response.data.page);
                }).catch(() => {
                    //Sweet alert telling them there was an error and that they should refresh
                    Swal.fire({
                        title: 'Error',
                        text: 'Something went wrong when trying to apply the filters. Please refresh the page and try again.',
                        icon: 'error',
                        confirmButtonText: "Refresh",
                        customClass: {
                            popup: 'swal-error'
                        },
                    }).then((result) => {
                        if (result.isConfirmed) {
                            location.reload();
                        }
                    });
                });
        },
        downloadChart(chart_div) {
            //Add the necessary modifications to canvas2svg library to work with chartjs
            Context.prototype.getContext = function (contextId) {
                if (contextId=="2d" || contextId=="2D")
                    return this;
                return null;
            }
            Context.prototype.style = function () { return this.__canvas.style }
            Context.prototype.getAttribute = function (name) { return this[name];}
            Context.prototype.addEventListener =  function() {}

            this.loading = true;
            window.axios.get('/api/export/sizes/' + this.client.id)
                .then(response => {
                    var sizes = response.data.sizes;

                    let container = document.getElementById(chart_div.id);
                    let chart = container.querySelector('canvas');
                    //Prep the chart
                    let size = container.dataset.size;
                    if(size == 'x-large' || size == 'large') {
                        chart.dataset.width = sizes.FULL_WIDTH.width;
                        chart.dataset.height = sizes.FULL_WIDTH.height;
                    }
                    else if(size == 'small'){
                        chart.dataset.width = sizes.SINGLE_SMALL.width;
                        chart.dataset.height = sizes.SINGLE_SMALL.height;
                    }
                    //By default, set it to the full width
                    else {
                        chart.dataset.width = sizes.FULL_WIDTH.width;
                        chart.dataset.height = sizes.FULL_WIDTH.height;
                    }

                    //If it is a chart
                    let svg = this.convertToSVG(chart);
                    svg = this.updateTextElements(svg);
                    if(container.querySelector('.line-chart-template') || container.querySelector('.bar-line-chart-template')){
                        svg = this.addLinesToChart(svg);
                    }

                    const chartjs = Chart.getChart(chart);
                    // Use filtered data (without hidden datasets) for export
                    let chart_data = this.getVisibleChartData(chartjs);

                    let data = {
                        client: this.client,
                        page: this.page,
                        params: (document.location.search!='') ? document.location.search.substring(1) : '',
                        svg: {
                            id: chart_div.id,
                            title: chart_div.chart.title,
                            subtitle: chart_div.chart.subtitle,
                            svg: svg,
                            data: chart_data,
                            size: size
                        }
                    };

                    window.axios.post('/api/export/chart', data, { responseType: 'blob' })
                        .then(response => {
                            // Create a URL for the blob and trigger a download
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;

                            let name = chart_div.chart.title + ' - Chart #' + chart_div.id + ' - ' + this.getDate() + '.pptx';

                            link.setAttribute('download', name);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            this.loading = false;
                        })
                        .catch(error => {
                            console.error(error);
                        });
            });
        },
        setActiveChart(chart, index) {
            this.activeChart = chart;
            this.activeChartIndex = index;
        },
        navigateToFilter(filter) {
            // Open the filter panel if it's not already open
            if (!this.is_filter_panel_expanded) {
                this.toggleFilterPanel();
            }
            
            // Use nextTick to ensure the filter panel is rendered before trying to navigate
            this.$nextTick(() => {
                if (this.$refs.filterPanel) {
                    this.$refs.filterPanel.navigateToFilter(filter);
                }
            });
        },
        getSegmentName() {
            let segment_name = '';
            if (this.applied_chart_filter && this.applied_chart_filter.if_chart_filter_applied) {
                this.applied_chart_filter.chart_filters.forEach((chart_filter) => {
                    segment_name += chart_filter.value + ' - ';
                });
                // Remove trailing ' - '
                segment_name = segment_name.slice(0, -3);
            }
            return segment_name;
        },
        setSegmentDetails(mode = 'audience') {
            this.segment_save_mode = mode;
            this.segment_selected.name = this.getDefaultSegmentName(mode);
            // Also forward to the active section component (back-compat)
            // Get section component from our map using the active tab's section ID
            const activeSection = this.local_page.sections[this.active_tab_index];
            const sectionComp = activeSection ? this.sectionComponents[activeSection.id] : null;
            if (sectionComp && typeof sectionComp.setSegmentDetails === 'function') {
                sectionComp.setSegmentDetails();
            }
        },
        getDefaultSegmentName(mode = 'audience') {
            const chartFilterSegmentName = this.getSegmentName();

            if (chartFilterSegmentName) {
                return chartFilterSegmentName;
            }

            if (mode === 'analysis_audience') {
                return this.page.name + ' Analysis Audience';
            }

            return this.page.name;
        },
        // ADD SEGMENT MODAL HELPERS
        getDefaultSize(sizes) {
            if (!sizes) return null;
            for (let i = 0; i < sizes.length; i++) {
                if (sizes[i].id === 0) return sizes[i].size;
            }
            return null;
        },
        changePathwaySelection(pathway) {
            this.pathway_selected = pathway;
        },
        clearErrors() {
            this.errors = {
                segment_name_push: '',
                segment_name_save: ''
            };
        },
        hasErrors() {
            if (!this.segment_selected.name || this.segment_selected.name.trim() === '') {
                this.errors.segment_name_push = 'Audience name is required.';
                this.errors.segment_name_save = 'Audience name is required.';
            } else {
                this.errors.segment_name_push = '';
                this.errors.segment_name_save = '';
            }
            return this.errors.segment_name_push !== '' || this.errors.segment_name_save !== '';
        },
        closeModal(id) {
            if (id === 'push-segment-modal-' + (this.active_page_section ? this.active_page_section.id : '')) {
                this.if_proceed_select_pathway = false;
            }
            const el = document.getElementById(id);
            if (!el) return;
            const modal = bootstrap.Modal.getInstance(el);
            if (modal) modal.hide();
        },
        buildInsightsSegmentPayload(createAnalysisAudience = false) {
            const page_copy = JSON.parse(JSON.stringify(this.local_page));
            page_copy.sections.forEach(sec => sec.charts.forEach(ch => { ch.data = null; }));

            return {
                page: page_copy,
                chart_id: this.applied_chart_filter?.if_chart_filter_applied ? this.applied_chart_filter.chart_selected.id : null,
                section_id: this.active_page_section ? this.active_page_section.id : null,
                segment_name: this.segment_selected.name,
                create_analysis_audience: createAnalysisAudience,
            };
        },
        proceedSelectPathway() {
            this.clearErrors();
            if (this.hasErrors()) return;
            this.if_proceed_select_pathway = true;
            const form_data = this.buildInsightsSegmentPayload();
            window.axios.post('/api/segment/' + this.client.url + '/push', form_data)
                .then(response => {
                    this.segment_selected = response.data.segment;
                });
        },
        directToPushSegmentPage() {
            const isAvidIntegration = this.pathway_selected.integration?.census_connection_type === 'avid';
            if (!isAvidIntegration && this.pathway_selected.census_destination_id == null && this.pathway_selected.integration?.census_requires_oauth) {
                const self = this;
                Swal.fire({
                    title: 'Additional Access Needed',
                    text: 'Avid has partnered with a software service named Census to activate the ability to push data to ' + this.pathway_selected.integration.name + '. This software requires an additional authentication before it can be used.',
                    iconHtml: '<i class="fas fa-exclamation"></i>',
                    showCancelButton: true,
                    confirmButtonText: 'Proceed',
                    customClass: { popup: 'swal-warning' }
                }).then(result => {
                    if (result.isConfirmed) {
                        window.location = '/integrations/' + self.client.url + '/census/oauth/' + self.pathway_selected.id + '/' + self.segment_selected.id;
                    }
                });
            } else {
                window.location = '/pathways/' + this.client.url + '/' + this.segment_selected.id + '/' + this.pathway_selected.id;
            }
        },
        saveSegment() {
            this.clearErrors();
            if (this.hasErrors()) return;
            const isAnalysisAudienceSave = this.segment_save_mode === 'analysis_audience';
            const form_data = this.buildInsightsSegmentPayload(isAnalysisAudienceSave);
            this.if_saving_segment = true;
            window.axios.post('/api/segment/' + this.client.url + '/push', form_data)
                .then(response => {
                    this.segment_selected = response.data.segment;
                    this.closeModal('save-segment-modal-' + (this.active_page_section ? this.active_page_section.id : ''));
                    
                    this.showNotification({
                        text: '<strong>' + this.segment_selected.name + '</strong> was saved as an analysis audience.',
                        type: 'success',
                        link: '/audience/' + this.client.url + '/' + this.segment_selected.id,
                    });
                    
                })
                .catch(error => {
                    this.showNotification({
                        text: error.response?.data?.error || error.response?.data?.message || 'Failed to save audience.',
                        type: 'error',
                    });
                })
                .finally(() => {
                    this.if_saving_segment = false;
                });
        },
        removedAppliedChartFilter() {
            const details = {
                if_chart_filter_applied: false,
                section_index: null,
                section_chart_id: null,
                chart_selected: {},
                chart_filters: []
            };
            this.exploreChartInSection(details);
        },
        toggleGroup(group) {
            if (group in this.collapsed_groups) {
                this.collapsed_groups[group] = !this.collapsed_groups[group];
            } else {
                this.collapsed_groups[group] = true;
            }
        },
        /* ---------------- Page Dropdown Helpers ---------------- */
        pagesByGroup() {
            const grouped = {};
            this.local_pages.forEach(p => {
                const g = p.group_name || 'Ungrouped';
                if (!grouped[g]) grouped[g] = [];
                grouped[g].push(p);
            });
            return grouped;
        },
        fetchPageOptions() {
             if (!this.search_page_key) {
                 // reset to full list and collapse again
                 this.local_pages = Array.isArray(this.all_pages) ? [...this.all_pages] : [...(this.all_pages.data || [])];
             } else {
                 const key = this.search_page_key.toLowerCase();
                 const base = Array.isArray(this.all_pages) ? this.all_pages : (this.all_pages.data || []);
                 this.local_pages = base.filter(p => {
                     const nameMatch = p.name.toLowerCase().includes(key);
                     const groupMatch = p.group_name ? p.group_name.toLowerCase().includes(key) : false;
                     return nameMatch || groupMatch;
                 });
             }
            // Update collapsed state: collapse when no search, expand when searching
            this.collapsed_groups = {};
            const shouldCollapse = this.search_page_key === '';
            Object.keys(this.pagesByGroup()).forEach(g => {
                this.collapsed_groups[g] = shouldCollapse;
            });
        },
        selectPage(pageObj) {
            if (!pageObj) return;
            // Navigation is now handled by Link component
            // This method is kept for backwards compatibility with keyboard navigation
            router.visit('/insights/' + this.client.url + '/' + pageObj.url);
        },
        handleSidebarStorageChange(e) {
            if (e.key === 'sidebar_collapsed') {
                this.sidebar_collapsed = e.newValue === 'true';
            }
        },
        handleSidebarToggleEvent() {
            this.sidebar_collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
        },
        fetchAllSectionsKeyTakeaways() {
            // Check if any section has active charts
            const hasActiveCharts = this.local_page.sections.some(section => {
                if (!section.charts || !Array.isArray(section.charts)) return false;
                return section.charts.some(chart => {
                    const chartData = chart.chart;
                    if (!chartData) return false;
                    // Skip textboxes
                    if (chartData.type === 'information_card' && chartData.html !== '') return false;
                    // Check for valid data
                    if (!chartData.data || Object.keys(chartData.data).length === 0) return false;
                    // Check for valid labels
                    if (!chartData.labels || chartData.labels.length === 0) return false;
                    if (chartData.labels.length === 1 && chartData.labels[0] === '(No Data)') return false;
                    return true;
                });
            });

            if (!hasActiveCharts) return;

            // Show edna loading for all charts that may get summaries until the API responds
            const summariesQueued = { ...this.chart_summaries };
            this.local_page.sections.forEach(section => {
                if (!section.charts || !Array.isArray(section.charts)) return;
                section.charts.forEach(chart => {
                    const chartData = chart.chart;
                    if (!chartData || !chart.id) return;
                    if (chartData.type === 'information_card' && chartData.html !== '') return;
                    if (!chartData.data || Object.keys(chartData.data).length === 0) return;
                    if (!chartData.labels || chartData.labels.length === 0) return;
                    if (chartData.labels.length === 1 && chartData.labels[0] === '(No Data)') return;
                    summariesQueued[chart.id] = { status: 'queued' };
                });
            });
            this.chart_summaries = summariesQueued;

            var form_data = {
                'page': JSON.parse(JSON.stringify(this.local_page)),
            }

            delete form_data['page'].client;
            if (form_data['page'].sections && Array.isArray(form_data['page'].sections)) {
                form_data['page'].sections.forEach(section => {
                    if (section.charts && Array.isArray(section.charts)) {
                        section.charts.forEach(chart => {
                            if (chart && chart.chart) {
                                delete chart.chart.data;
                                delete chart.chart.labels;
                                delete chart.chart.metric_formats;
                            }
                        });
                    }
                });
            }
            
            window.axios.post('/api/insights/page/'+this.local_page.id+'/takeaways', form_data)
                .then(response => {
                    if (response.data.section_takeaways && typeof response.data.section_takeaways === 'object') {
                        Object.entries(response.data.section_takeaways).forEach(([sectionId, payload]) => {
                            this.applySectionTakeawayPayload(sectionId, payload);
                        });
                    }
                    // Bundled x-large chart summaries (completed or queued)
                    if (response.data.chart_summaries && typeof response.data.chart_summaries === 'object') {
                        this.chart_summaries = { ...this.chart_summaries, ...response.data.chart_summaries };
                    }
                    // Queued jobs will deliver via ChartSummaryUpdated Echo event
                })
                .catch(error => {
                    console.error('Error fetching key takeaways for all sections:', error);
                });
        },
        applySectionTakeawayPayload(sectionId, payload) {
            if (!payload || sectionId == null || !this.local_page?.sections) {
                return;
            }

            const normalizedSectionId = Number(sectionId);
            const section = this.local_page.sections.find(s => Number(s.id) === normalizedSectionId);
            if (!section) {
                return;
            }

            if (payload.status === 'completed') {
                const data = {
                    summary: payload.summary ?? null,
                    takeaways: Array.isArray(payload.takeaways) ? payload.takeaways : [],
                    related_charts: Array.isArray(payload.related_charts) ? payload.related_charts : [],
                };

                section.summary = data.summary;
                section.takeaways = data.takeaways;
                section.related_charts = data.related_charts;

                const sectionComponent = this.sectionComponents[normalizedSectionId];
                if (sectionComponent) {
                    sectionComponent.updateInsights(data);
                }

                return;
            }

            if (payload.status === 'failed' || payload.status === 'timeout') {
                section.summary = null;
                section.takeaways = [];
                section.related_charts = [];

                const sectionComponent = this.sectionComponents[normalizedSectionId];
                if (sectionComponent) {
                    sectionComponent.updateInsights(null);
                }
            }
        },
        onChartSummaryQueued(chartId) {
            if (chartId == null) return;
            this.chart_summaries = { ...this.chart_summaries, [chartId]: { status: 'queued' } };
        },
        onChartSummaryCompleted({ chartId, summary, related_charts }) {
            if (chartId == null) return;
            this.chart_summaries = {
                ...this.chart_summaries,
                [chartId]: { summary: summary ?? null, status: 'completed', related_charts: related_charts ?? [] }
            };
        },
        setupKeyTakeawaysListener() {
            // Set up a single client-wide listener for all key takeaways updates
            // Demo account uses client.id === 0; 0 is falsy so we must check explicitly
            const clientId = this.client?.id;
            if (this.if_benchmark_report || (clientId !== 0 && !clientId)) {
                return;
            }

            const self = this;
            this.echoChannel = window.Echo.private('client.' + clientId)
                .listen('KeyTakeawaysUpdated', (e) => {
                    if (!self || !self.isMounted || !self.local_page || !self.local_page.sections) return;
                    try {
                        const sectionId = e.section_id;
                        const section = self.local_page.sections.find(s => s.id == sectionId);
                        if (!section) return;
                        const sectionComponent = self.sectionComponents[sectionId];
                        if (sectionComponent) {
                            if (e.status == 'completed') {
                                const data = e.takeaways || {};
                                if (e.related_charts) data.related_charts = e.related_charts;
                                section.summary = data.summary ?? null;
                                section.takeaways = data.takeaways ?? [];
                                section.related_charts = data.related_charts || [];
                                sectionComponent.updateInsights(data);
                            } else if (e.status == 'failed' || e.status == 'timeout') {
                                section.summary = null;
                                section.takeaways = [];
                                section.related_charts = [];
                                if (sectionComponent.local_section) {
                                    sectionComponent.local_section.takeaways = [];
                                    sectionComponent.local_section.summary = null;
                                    sectionComponent.local_section.related_charts = [];
                                }
                            }
                        }
                    } catch (err) {
                        if (self && self.isMounted) console.warn('Echo callback error:', err);
                    }
                })
                .listen('ChartSummaryUpdated', (e) => {
                    if (!self || !self.isMounted || !self.chart_summaries) return;
                    try {
                        const chartId = e.chart_id;
                        if (e.status === 'completed' && chartId != null) {
                            self.chart_summaries = {
                                ...self.chart_summaries,
                                [chartId]: {
                                    summary: e.summary ?? null,
                                    status: 'completed',
                                    related_charts: e.related_charts ?? []
                                }
                            };
                        } else if (e.status === 'failed' && chartId != null) {
                            self.chart_summaries = {
                                ...self.chart_summaries,
                                [chartId]: { status: 'failed' }
                            };
                        }
                    } catch (err) {
                        if (self && self.isMounted) console.warn('ChartSummaryUpdated error:', err);
                    }
                });
        }
    }
});
</script>